import React, { createContext, useState, useEffect, useContext } from "react";
import { useCartContext } from "./CartContext"; // Use CartContext instead of managing own cart
import { OrderContextType, HandleOrderType, OrderDataProps } from "../types/order.types";
import { defaultOrderData } from "../constants/orderData";
import {
    getDateISO,
    parseDMY,
    parseDateFromAnyFormat,
    filterDatesOnOrAfterEffectiveTomorrow,
    getEffectiveTomorrowISO,
} from "../utils/calendar";
import { useUserContext } from "./UserContext";
import { slashDMYtoISO } from "../utils/calendar";
import { usePublicCatalog } from "./PublicCatalogContext";
import { log } from "../utils/logger";
import { defaultOrderTime } from "../constants/times";
/**
* React context for managing order-related state, including order type,
* order details, delivery time, and access to cart information.
*/
const OrderContext = createContext<OrderContextType | undefined>(undefined);

/**
* Provider component for OrderContext.
* 
* Manages:
* - `orderType`: "order" or "demand" based on cart contents.
* - `orderData`: user-entered order details, synced with sessionStorage.
* - `deliveryTime`: delivery time string, synced with sessionStorage.
* 
* Uses CartContext for all cart data and operations.
*/
type CartItem = { category: string; [k: string]: unknown };
type CartItemsByDate = Record<string, CartItem[]>;

export const OrderProvider = ({ children }: { children: React.ReactNode }) => {
    const { uuid, setUuid, isPublicView } = usePublicCatalog();
    
    // Cart data from CartContext - no more managing own cart state!
    const { cartData, cartLoaded, reloadCart } = useCartContext();
    
    // Order type
    const [orderType, setOrderType] = useState<"order"|"demand">("order");
    
    const { loggedIn, userData } = useUserContext();
    // Order data
    const [orderData, setOrderData] = useState<OrderDataProps>(() => {
        const saved = sessionStorage.getItem("orderData");
        return saved ? JSON.parse(saved) : defaultOrderData;
    });

    useEffect(() => {
        if (!loggedIn && orderData !== defaultOrderData) {
            //log("Reseting order data: no user found...")
            resetOrderData()
        }
    },[loggedIn])
    const resetOrderData = () => {
        setOrderData(defaultOrderData)
        sessionStorage.removeItem("orderData")  
    }

    useEffect(() => {
        // Snad facha
        if (!orderData || !cartLoaded || !cartData) return;

        // Build cartDatesISO try: selected_dates -> items -> time.from/to
        let cartDatesISO: string[] = [];

        if (Array.isArray(cartData.selected_dates) && cartData.selected_dates.length > 0) {
            cartDatesISO = cartData.selected_dates
            .map((d: string) => {
                const parsed = parseDMY(d); // expects "D. M. YYYY"
                return parsed ? getDateISO(parsed) : null; // "YYYY-MM-DD" or null
            })
            .filter((s): s is string => Boolean(s));
        } else if (cartData.items && Object.keys(cartData.items).length > 0) {
            cartDatesISO = Object.keys(cartData.items)
            .map(k => {
                const parsed = parseDMY(k);
                return parsed ? getDateISO(parsed) : null;
            })
            .filter((s: string | null): s is string => Boolean(s));
        } else if (cartData.time?.from && cartData.time?.to) {
            const start = slashDMYtoISO(cartData.time.from); // "D/M/YYYY" -> "YYYY-MM-DD"
            const end   = slashDMYtoISO(cartData.time.to);
            cartDatesISO = start === end ? [start] : [start, end];
        } else {
            // nothing to sync
            return;
        }

        // Normalize: dedupe + sort to make comparisons stable
        cartDatesISO = Array.from(new Set(cartDatesISO)).sort();
        const validCartDatesISO = filterDatesOnOrAfterEffectiveTomorrow(cartDatesISO).sort();

            // Ensure orderData.date is in ISO format (it should already be)
            const orderDatesISO = orderData.date.map(dateStr => {
                // If it's already ISO format, return as is
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    return dateStr;
                }
                // If it's in a different format, try to parse and convert
                const parsed = parseDateFromAnyFormat(dateStr);
                return parsed ? getDateISO(parsed) : dateStr;
            });

            // Check if arrays are different
            const datesAreDifferent = 
                validCartDatesISO.length !== orderDatesISO.length ||
                !validCartDatesISO.every((date, index) => date === orderDatesISO[index]);

            if (datesAreDifferent && validCartDatesISO.length > 0) {
                //log('Syncing dates from cart to order...');
                handleOrder("date", validCartDatesISO);
            }
            
            if (orderData.address !== cartData.delivery_adress.adress) {
                //log("Syncing address...")
                handleOrder("address", cartData.delivery_adress.adress)
            }
            if (Number(orderData.customer) !== Number(cartData.person_id)) {
                //log("Syncing user...")
                handleOrder("customer", cartData.person_id.toString())
            }
            if (Number(orderData.recipient) !== Number(cartData.subscriber_id)) {
                //log("Syncing recipient...")
                handleOrder("recipient", cartData.subscriber_id)
            }
        
    }, [cartLoaded, cartData]);
    

    /**
    * Updates orderData by key and value.
    * Persists the updated orderData to sessionStorage.
    * 
    * @param key - The key in orderData to update
    * @param value - The new value to set
    */
    const handleOrder: HandleOrderType = (key, value) => {
        if (key === "date") {
            //log(value)
        }
        setOrderData((prev) => {
            const updated = { ...prev, [key]: value };
            sessionStorage.setItem("orderData", JSON.stringify(updated));
            return updated;
        });
    }

    // Change selected date on update
    useEffect(() => {
        if (!orderData.date || orderData.date.length === 0) return;
        const validDates = filterDatesOnOrAfterEffectiveTomorrow(orderData.date).sort();
        const preferred = validDates[0] ?? getEffectiveTomorrowISO();

        const needsUpdate =
            !orderData.selectedDate ||
            !validDates.includes(orderData.selectedDate);

        if (needsUpdate && orderData.selectedDate !== preferred) {
            handleOrder("selectedDate", preferred);
        }
    }, [orderData.date, orderData.selectedDate]);

    // Delivery time
    const [deliveryTime, setDeliveryTime] = useState(() => {
        const saved = sessionStorage.getItem("deliveryTime");
        return saved ? JSON.parse(saved) : defaultOrderTime;
    });

    useEffect(() => {
        sessionStorage.setItem("deliveryTime", JSON.stringify(deliveryTime));
    }, [deliveryTime]);

    // Update order type on cart change
    useEffect(() => {
        if (!cartData) return;
        // Look through all dates and items to find venue/extra types
        const hasVenueOrExtra = Object.values(cartData.items).some(dateItems => 
            dateItems.some(item => item.category === "venue" || item.category === "storage" || item.category === "activities")
        );
    

        
        if (hasVenueOrExtra) {
            setOrderType("demand")
        } else {
            setOrderType("order")
        }
    }, [cartData])

    useEffect(() => {
        if (isPublicView) return;

        const recipientCandidate =
            orderData.recipient ??
            cartData?.subscriber_id ??
            userData?.subscribers?.[0]?.subscriber_id;
        if (!recipientCandidate && userData?.subscribers?.[0]?.uuid && userData.subscribers[0].uuid !== uuid) {
            setUuid(userData.subscribers[0].uuid);
            return;
        }
        if (!recipientCandidate) return;

        const recipientId = Number(recipientCandidate);
        if (!Number.isFinite(recipientId)) return;

        const subscriber = userData?.subscribers?.find(
            (sub) => Number(sub.subscriber_id) === recipientId
        );

        if (subscriber?.uuid && subscriber.uuid !== uuid) {
            setUuid(subscriber.uuid);
        }
    }, [
        isPublicView,
        orderData.recipient,
        cartData?.subscriber_id,
        userData?.subscribers,
        uuid,
        setUuid,
    ]);

    return (
        <OrderContext.Provider value={{ setDeliveryTime, deliveryTime, orderData, handleOrder, orderType, resetOrderData }}>
            {children}
        </OrderContext.Provider>
    );
}

/**
* Custom hook to access OrderContext.
* Throws error if used outside of an OrderProvider.
* 
* @returns The current order context including state and handlers.
*/
export const useOrderContext = () => {
    const context = useContext(OrderContext);
    if (!context) throw new Error("useOrderContext must be used within a OrderProvider");
    return context;
};



